import React from 'react';
import { cssMap } from '@compiled/react';
import Skeleton from '@atlaskit/skeleton';
import { Box } from '@atlaskit/primitives/compiled/box';
import { Inline } from '@atlaskit/primitives/compiled/inline';
import { Stack } from '@atlaskit/primitives/compiled/stack';
import { token } from '@atlaskit/tokens';
import VisuallyHidden from '@atlaskit/visually-hidden/visually-hidden';
import { useIntl, defineMessage } from '@atlassian/jira-intl';
import { CapacityListSkeleton } from './CapacityListSkeleton.tsx';

const loadingMessage = defineMessage({
	id: 'servicedesk-capacity.capacity-page-skeleton.loading',
	defaultMessage: 'Loading capacity page',
	description: 'Accessible loading message for screen readers while the capacity page is loading',
});

const styles = cssMap({
	container: {
		width: '100%',
	},
	headerSkeleton: {
		marginBottom: token('space.100'),
	},
	breadcrumbSkeleton: {
		marginBottom: token('space.200'),
	},
	cardRow: {
		width: '100%',
	},
	metricCard: {
		flex: 1,
		minWidth: '200px',
		height: '80px',
	},
});

/**
 * Skeleton loading state for the Capacity page.
 * Displays placeholder content while the page data is being loaded.
 */
export const CapacityPageSkeleton = (): React.JSX.Element => {
	const { formatMessage } = useIntl();

	return (
		<Box padding="space.300" xcss={styles.container}>
			<Stack space="space.200">
				<VisuallyHidden>{formatMessage(loadingMessage)}</VisuallyHidden>

				{/* Breadcrumb skeleton */}
				<Box xcss={styles.breadcrumbSkeleton}>
					<Skeleton isShimmering width="200px" height="16px" />
				</Box>

				{/* Page title skeleton */}
				<Box xcss={styles.headerSkeleton}>
					<Skeleton isShimmering width="120px" height="32px" />
				</Box>

				{/* Filter row skeleton */}
				<Inline space="space.100" alignBlock="center">
					<Skeleton isShimmering width="100px" height="32px" />
					<Skeleton isShimmering width="100px" height="32px" />
					<Skeleton isShimmering width="160px" height="32px" />
					<Skeleton isShimmering width="90px" height="32px" />
				</Inline>

				{/* Top metric cards row - 3 cards */}
				<Inline space="space.200" spread="space-between" xcss={styles.cardRow}>
					<Box xcss={styles.metricCard}>
						<Skeleton
							isShimmering
							width="100%"
							height="100%"
							borderRadius={token('radius.large')}
						/>
					</Box>
					<Box xcss={styles.metricCard}>
						<Skeleton
							isShimmering
							width="100%"
							height="100%"
							borderRadius={token('radius.large')}
						/>
					</Box>
					<Box xcss={styles.metricCard}>
						<Skeleton
							isShimmering
							width="100%"
							height="100%"
							borderRadius={token('radius.large')}
						/>
					</Box>
				</Inline>

				{/* Capacity list skeleton (filters + agent rows) */}
				<CapacityListSkeleton />
			</Stack>
		</Box>
	);
};
